import { ApiTarget, HttpClient } from '@packages/core/http';
import { RepairOrder } from '@packages/models/api';
import {
    Campaign,
    CampaignsByVinResponseDto,
    DmsRepairOrderHistoryResponseDto,
    LatestOpenCampaignResponseDto,
    ViewCampaignDocumentResponseDto,
} from '@packages/models/api/dms';
import { DmsActionDispatcher, dmsActions } from './dms.state';

export class DmsService {
    constructor(private http: HttpClient) {}

    async fetchRepairOrderHistory(vin: string, ignoreCache = false): Promise<RepairOrder[]> {
        const response = await this.http.orchestrateRequest<DmsRepairOrderHistoryResponseDto>({
            method: 'GET',
            url: `/northstar/repair-order-history/${vin}`,
            ignoreCache,
            apiTarget: ApiTarget.VinService,
        });

        if (!response.success) {
            return Promise.reject(response.data);
        }

        if (!response.data.repairOrders?.length) {
            return [];
        }
        return response.data.repairOrders;
    }

    async fetchLatestRepairOrder(vin: string, ignoreCache = false): Promise<RepairOrder> {
        const response = await this.http.orchestrateRequest<RepairOrder>({
            method: 'GET',
            url: `/northstar/latest-repair-order/${vin}`,
            ignoreCache,
            apiTarget: ApiTarget.VinService,
        });

        if (!response.success) {
            return Promise.reject(response.data);
        }

        return response.data;
    }

    async fetchCampaignsByVin(vin: string, ignoreCache = false): Promise<Campaign[]> {
        const response = await this.http.orchestrateRequest<CampaignsByVinResponseDto>({
            method: 'GET',
            url: `/northstar/campaigns/${vin}`,
            ignoreCache,
            apiTarget: ApiTarget.VinService,
        });

        if (!response.success) {
            return Promise.reject(response.data);
        }

        if (!response.data.campaigns?.length) {
            return [];
        }
        return response.data.campaigns;
    }

    async fetchLatestOpenCampaigns(vin: string, ignoreCache = false): Promise<LatestOpenCampaignResponseDto> {
        const response = await this.http.orchestrateRequest<LatestOpenCampaignResponseDto>({
            method: 'GET',
            url: `/northstar/latest-open-campaign/${vin}`,
            ignoreCache,
            apiTarget: ApiTarget.VinService,
        });

        if (!response.success) {
            return Promise.reject(response.data);
        }

        return response.data;
    }

    // Mobile-only: returns the S3 presigned URL (plain text) for a campaign publication.
    // Mobile downloads the PDF locally (Android) and cannot use the CDN signed-cookie flow,
    // so it stays on /view-campaign-doc. Web uses the CDN endpoint via fetchCdnCampaignDocUrl.
    async fetchCampaignPublicationUrl(campaignCode: string): Promise<{ success: boolean; data?: string } | undefined> {
        const response = await this.http.orchestrateRequest<ViewCampaignDocumentResponseDto>({
            method: 'GET',
            url: `/northstar/view-campaign-doc/${campaignCode}`,
            apiTarget: ApiTarget.VinService,
            responseType: 'text',
        });

        if (typeof response.data === 'string' && (response.data as string).trim().length > 0) {
            return { success: response.success, data: response.data as string };
        }
    }

    async fetchAndSetLatestRepairOrder(vin: string, dispatch: DmsActionDispatcher) {
        dispatch(dmsActions.isLoading());
        const latestOrder = await this.fetchLatestRepairOrder(vin);
        dispatch(dmsActions.setLatestOrder({ latestOrder }));
        dispatch(dmsActions.hasLoaded());
        dispatch(dmsActions.hasFetched());
    }

    async fetchAndSetRepairOrderHistory(vin: string, dispatch: DmsActionDispatcher) {
        dispatch(dmsActions.isLoading());
        const repairOrders = await this.fetchRepairOrderHistory(vin);
        dispatch(dmsActions.setRepairOrdersHistory({ repairOrders }));
        dispatch(dmsActions.setDmsCount());
        dispatch(dmsActions.hasLoaded());
        dispatch(dmsActions.hasHistoryFetched());
    }

    async fetchAndSetCampaignsByVin(vin: string, dispatch: DmsActionDispatcher) {
        dispatch(dmsActions.isCampaignLoading({ isLoading: true }));
        const campaignsByVin = await this.fetchCampaignsByVin(vin);
        dispatch(dmsActions.setCampaignsByVin({ campaignsByVin }));
        dispatch(dmsActions.setCampaignCount());
        dispatch(dmsActions.isCampaignLoading({ isLoading: false }));
        dispatch(dmsActions.hasCampaignHistoryFetched());
    }

    async fetchAndSetLatestOpenCampaign(vin: string, dispatch: DmsActionDispatcher) {
        dispatch(dmsActions.isLatestCampaignLoading({ isLoading: true }));
        const { latestOpenCampaign, totalOpenCampaigns } = await this.fetchLatestOpenCampaigns(vin);
        dispatch(dmsActions.setLatestOpenCampaign({ latestOpenCampaign }));
        dispatch(
            dmsActions.setOpenCampaignCount({
                openCampaignCount: totalOpenCampaigns ? parseInt(totalOpenCampaigns, 10) : 0,
            })
        );
        dispatch(dmsActions.isLatestCampaignLoading({ isLoading: false }));
        dispatch(dmsActions.hasLatestCampaignFetched());
    }
}
